/**
 * EventWidget Component
 *
 * Pannello laterale per creazione/modifica eventi.
 * Gestisce la creazione e modifica di eventi tramite GraphQL mutations.
 *
 * Pattern: DetailPanel (pannello laterale destro, mobile = full screen)
 */

import { useMemo, useState } from 'react';
import { Calendar, Bell, Target } from 'lucide-react';
import {
  useCalendarEvents,
  useCreateEvent,
  useUpdateEvent,
  type CalendarEvent,
  type EventType,
} from '@business-assistant/shared';
import { DetailPanel } from '../common/base';
import { EventForm, type EventFormData } from '../forms/EventForm';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';

// =============================================================================
// TYPES
// =============================================================================

interface EventWidgetProps {
  isOpen: boolean;
  eventId?: string;
  initialDate?: string;
  initialEventType?: EventType;
  initialTaskId?: string;
  initialProjectId?: string;
  onClose: () => void;
  onSave?: (event: CalendarEvent) => void;
}

// Re-export EventFormData for backwards compatibility
export type { EventFormData };

// =============================================================================
// COMPONENT
// =============================================================================

export function EventWidget({
  isOpen,
  eventId,
  initialDate,
  initialEventType,
  initialTaskId,
  initialProjectId,
  onClose,
  onSave,
}: EventWidgetProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: events = [] } = useCalendarEvents();
  const createEvent = useCreateEvent();
  const updateEvent = useUpdateEvent();

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const existingEvent = useMemo(() => {
    if (!eventId) return undefined;
    return events.find((e) => e.id === eventId);
  }, [eventId, events]);

  const isEditMode = !!eventId;

  // Determine icon and color based on event type
  const getHeaderProps = () => {
    const eventType = existingEvent?.eventType || initialEventType || 'event';
    switch (eventType) {
      case 'reminder':
        return { icon: <Bell size={18} />, color: '#f59e0b' };
      case 'milestone':
        return { icon: <Target size={18} />, color: '#ec4899' };
      default:
        return { icon: <Calendar size={18} />, color: '#22c55e' };
    }
  };

  const headerProps = getHeaderProps();

  const handleSubmit = async (formData: EventFormData) => {
    setError(null);
    setIsSubmitting(true);

    try {
      // Transform form data to API format
      const eventData = {
        eventType: formData.eventType,
        title: formData.title,
        description: formData.description || undefined,
        startAt: new Date(formData.startAt).toISOString(),
        endAt: formData.endAt ? new Date(formData.endAt).toISOString() : undefined,
        allDay: formData.allDay,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        mode: formData.mode,
        location: formData.location || undefined,
        meetingUrl: formData.meetingUrl || undefined,
        recurrence: formData.recurrence,
        organizerReminders: formData.organizerReminders || [],
        participants: formData.participants?.map(p => ({
          ...p,
          status: p.status || 'pending',
        })) || [],
        linkedTaskId: formData.linkedTaskId || undefined,
        projectId: formData.projectId || undefined,
        linkedTaskIds: formData.linkedTaskIds || [],
        contactId: formData.contactId || undefined,
        color: formData.color || undefined,
      };

      let result: CalendarEvent;

      if (isEditMode && eventId) {
        // Update existing event
        result = await updateEvent.mutateAsync({
          id: eventId,
          updates: eventData,
        });
      } else {
        // Create new event
        result = await createEvent.mutateAsync(eventData as any);
      }

      // Call onSave callback with the created/updated event
      if (onSave) {
        onSave(result);
      }

      onClose();
    } catch (err) {
      console.error('Failed to save event:', err);
      setError(err instanceof Error ? err.message : t('events.saveFailed', 'Failed to save event'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DetailPanel
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? t('events.editEvent') : t('events.newEvent')}
      subtitle={existingEvent?.title || t('events.createNewEvent')}
      icon={headerProps.icon}
      headerColor={headerProps.color}
      width="50%"
    >
      {error && (
        <div style={{
          padding: '0.75rem',
          marginBottom: '1rem',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '0.375rem',
          color: '#dc2626',
          fontSize: '0.875rem',
        }}>
          {error}
        </div>
      )}

      <EventForm
        initialData={existingEvent}
        initialDate={initialDate}
        initialEventType={initialEventType}
        initialTaskId={initialTaskId}
        initialProjectId={initialProjectId}
        onSubmit={handleSubmit}
        onCancel={onClose}
        submitLabel={isSubmitting ? t('common.saving', 'Saving...') : undefined}
      />
    </DetailPanel>
  );
}
